import BACKENDERROR from "app/static/frontend/imports/backenderror"
import Immutable from "immutable"
import actionTypes, * as actionGroups from "accounts/account-action-types"
import { deactivateOrgActionTypes } from "app/static/frontend/accounts/account-action-types"
import globalActionTypes from "global-action-types"
import { createEmptyOrg, createEmptyTeam, createEmptyAdminUser } from "accounts/account-reducer"
import * as djangioActionCreators from "shared/djangio/action-creators"
import { sendUserAction } from "shared/user-actions/user-actions-action-creators"
import { getIsLoadingUserdataUser } from "shared/userdata/userdata-selectors"
import { sendAction } from "shared/djangio/action-creators"
import swal from "sweetalert"
import { setSubmissionStatus } from "app/static/frontend/accounts/account-information-action-creators"
const { Organization } = DjangIO.app.userdata.models

/**
 * Send a Tastypie action to resend an activation email
 * @param {Integer} userId - the user id to send an activation email for
 * @returns {Promise} - result of action
 */
export const resendActivationEmail = (userId, organization_id) =>
    djangioActionCreators.resourcePatchAction(
        DjangIO.app.userdata.models.QuorumUser,
        actionGroups.resendActivationEmailTypes,
        userId,
        // decorator in API needs organization in this format
        { actionName: "resend_activation_email", userId, organization: organization_id },
        { payload: userId, organization: organization_id, default_for_user: false },
    )

export const getOrg = (id, extraFilters = {}) =>
    djangioActionCreators.resourceGetDetail(
        DjangIO.app.userdata.models.Organization.objects.filter({
            full_dehydrate_phone_numbers: true,
            ...extraFilters,
        }),
        actionGroups.getOrgTypes,
        id,
    )

export const getTeam = (id) =>
    djangioActionCreators.resourceGetDetail(DjangIO.app.userdata.models.Team, actionGroups.getTeamTypes, id)

/**
 * This function makes a request to get the teams for organization orgId
 * These Teams are used to set the default teams for the User created / edited
 * @name getPossibleTeams
 * @function
 * @param {Integer} orgId - organization ID
 * @returns {Promise}
 */
export const getPossibleTeams = (orgId) => {
    if (!orgId) {
        console.warn("orgId is undefined - this will result in undetermined teams returned")
    }
    return djangioActionCreators.resourceGetList(
        DjangIO.app.userdata.models.Team,
        actionGroups.getTeamsForUserFormTypes,
        {
            default_for_user: false,
            limit: 150,
            organization: orgId,
        }, //filters
        { orgId },
    )
}

export const createTeam = (teamKwargs) => (dispatch) => {
    dispatch(sendUserAction(DjangIO.app.useractions.types.UserActionType.team_created, teamKwargs.name))
    return dispatch(
        djangioActionCreators.resourcePostList(
            DjangIO.app.userdata.models.Team,
            actionGroups.createTeamTypes,
            teamKwargs,
        ),
    )
}

export const patchTeam = (id, team) => (dispatch) => {
    dispatch(sendUserAction(DjangIO.app.useractions.types.UserActionType.team_edited, team.name))
    return dispatch(
        djangioActionCreators.resourcePatchDetail(
            DjangIO.app.userdata.models.Team,
            actionGroups.patchTeamTypes,
            id,
            team,
        ),
    )
}

export const getEmptyOrg = () => ({ type: actionTypes.GET_ORG_SUCCESS, response: createEmptyOrg() })

export const getEmptyTeam = (orgId) => (dispatch) => {
    dispatch({ type: actionTypes.GET_TEAM_SUCCESS, response: createEmptyTeam() })
    return dispatch(getOrg(orgId))
}

// initialize the user form, called when we are creating a new user from scratch
export const initUserForm = () => ({ type: actionTypes.INIT_USER_FORM })

export const getEmptyAdminUser = () => ({ type: actionTypes.GET_USER_SUCCESS, response: createEmptyAdminUser() })

// Sends files to S3 Securely
export const secureUpload = (event) => {
    const { files } = event.target
    const data = new FormData()
    for (let key = 0; key < files.length; key++) {
        const file = files[key]
        data.append(key, file)
    }
    $.ajax({
        url: "/secure_upload/",
        type: "POST",
        data,
        cache: false,
        processData: false,
        contentType: false,
        success: () => {
            swal("Success", "Your file has been securely uploaded to our servers.", "success")
        },
        error(jqXHR) {
            if ([400, 422].includes(jqXHR.status) && jqXHR.responseJSON?.message) {
                swal("Error", jqXHR.responseJSON.message, "error")
            } else {
                swal(
                    "Error",
                    "We were unable to upload your file. It was either too large, an unsupported type, or there was some other error. Please contact Support for help.",
                    "error",
                )
            }
        },
    })
    return { type: actionTypes.SECURE_UPLOAD }
}

export const setValid = (isValid) => ({
    type: actionTypes.SET_VALID,
    isValid,
})

// Creates a user using the fields passed to it
export const createUser = (userKwargs) => async (dispatch) => {
    dispatch({ type: actionTypes.CREATE_USER_START })

    try {
        // If PAC management is enabled, validate email first
        if (userKwargs.can_access_pac_management) {
            const pacValidationActionTypes = {
                start: "VALIDATE_EMAIL_PAC_CLASSIC_START",
                success: "VALIDATE_EMAIL_PAC_CLASSIC_SUCCESS",
                fail: "VALIDATE_EMAIL_PAC_CLASSIC_FAIL",
            }

            try {
                await dispatch(
                    sendAction(DjangIO.app.userdata.models.QuorumUser, pacValidationActionTypes, {
                        action: "validate_email_for_pac_classic",
                        method: "post",
                        kwargs: { email: userKwargs.email },
                    }),
                )
                dispatch(setSubmissionStatus("", ""))
            } catch (validationError) {
                const errorMessage = validationError.response?.data?.message
                dispatch(setSubmissionStatus("error", errorMessage))
                throw validationError
            }
        }

        const response = await DjangIO.app.userdata.models.QuorumUser.objects
            .action("create_user")
            .update({ userKwargs })
            .PATCH()

        if (userKwargs.can_access_pac_management) {
            const userId = response.data?.user_id || response.user_id
            if (userId) {
                const pacActionTypes = {
                    start: "CREATE_PAC_CLASSIC_USER_START",
                    success: "CREATE_PAC_CLASSIC_USER_SUCCESS",
                    fail: "CREATE_PAC_CLASSIC_USER_FAIL",
                }

                await dispatch(
                    sendAction(DjangIO.app.userdata.models.QuorumUser, pacActionTypes, {
                        action: "create_pac_classic_user_if_needed",
                        method: "post",
                        kwargs: { user_id: userId },
                    }),
                )
            } else {
                console.error("No user_id found in response, cannot create PAC Classic user")
                dispatch({ type: actionTypes.CREATE_USER_FAIL })
                throw new Error("No user_id found in response, cannot create PAC Classic user")
            }
        }

        swal("Change Complete", "The user has been created", "success")
        dispatch({ type: actionTypes.CREATE_USER_SUCCESS })
    } catch (error) {
        dispatch({ type: actionTypes.CREATE_USER_FAIL })
        throw error
    }
}

export const patchUser = (id, kwargs) =>
    djangioActionCreators.resourcePatchDetail(
        DjangIO.app.userdata.models.QuorumUser.objects.filter(Userdata.is_staff ? { default_for_user: false } : {}),
        actionGroups.patchUserTypes,
        id,
        kwargs,
    )

const SUCCESS_SWAL = () => swal("Change Complete", "Your account settings have been changed", "success")

// Shows the user a Two Factor QR code
// and allows them to check if the
// Google authenticator code they get is correct
// before activating 2FA
export const twoFactorChange = (inputValue, tokenRequested, twoFactorStatus) => (dispatch) => {
    return DjangIO.app.userdata.models.QuorumUser.objects
        .action("two_factor")
        .update({ inputValue, tokenRequested, twoFactorStatus })
        .PATCH()
        .then((response) => {
            // if we need to activate the token
            if (response.tokenRequested) {
                return swal({
                    title: "QR Verification Token",
                    text: "Please download the Google Authenticator app to scan the QR code above and receive your constantly updating key.",
                    icon: "/account/qrcode/",
                    imageWidth: 250,
                    imageHeight: 250,
                    content: {
                        element: "input",
                        attributes: {
                            placeholder: "Enter your token here...",
                            type: "text",
                        },
                    },
                    buttons: {
                        cancel: true,
                        confirm: {
                            closeModal: false,
                        },
                    },
                }).then((token) => {
                    if (token === false) {
                        return false
                    } else if (token === "") {
                        swal("Please provide an input")
                        return false
                    } else if (!token) {
                        return false
                    } else {
                        return DjangIO.app.userdata.models.QuorumUser.objects
                            .action("two_factor")
                            .update({ inputValue, twoFactorStatus, token })
                            .PATCH()
                            .then((response) => {
                                if (response.responseText === "Success") {
                                    SUCCESS_SWAL()
                                    dispatch({
                                        type: globalActionTypes.VERIFY_TIME,
                                        time: response.verifyTime,
                                    })
                                    dispatch(editUserUiState("oldTwoFactorStatus", twoFactorStatus))
                                }
                            })
                    }
                })
                // if we want to deactivate the token
            } else if (response.responseText === "Success") {
                SUCCESS_SWAL()
                dispatch(editUserUiState("oldTwoFactorStatus", twoFactorStatus))
            }
        })
        .catch((err) => {
            swal("Change Failed", err.response.data.responseText, "warning")
        })
}

// Update the information of a user to the DB
export const updateInfo = (user, toggleTwoFactor, passwordConfirm) => (dispatch) => {
    dispatch({ type: actionTypes.PATCH_USER_START })
    return DjangIO.app.userdata.models.QuorumUser.objects
        .action("change_info")
        .filter(Userdata.is_staff ? { default_for_user: false } : {})
        .update({ passwordConfirm, ...user })
        .PATCH()
        .then((response) => {
            SUCCESS_SWAL()
            dispatch({
                type: globalActionTypes.VERIFY_TIME,
                time: response.verifyTime,
            })
            dispatch({ type: actionTypes.PATCH_USER_SUCCESS })
        })
}

// this function is very slow on the backend
// it creates an admin, his/her userinfo, the org itself, and lastly the default team
export const createOrg = (organization) => (dispatch) => {
    return DjangIO.app.userdata.models.Organization.objects
        .filter(Userdata.is_staff ? { default_for_user: false, include_quorum_employees: true } : {})
        .action("create_org")
        .update({ organization })
        .PATCH()
        .then(() => {
            swal("Change Complete", "The organization has been created", "success")
            dispatch({ type: actionTypes.UPDATE_ORG })
        })
}

// Change the permissions and information of the given Org
export const patchOrg = (id, org) =>
    djangioActionCreators.resourcePatchDetail(
        DjangIO.app.userdata.models.Organization.objects.filter(
            Userdata.is_staff ? { default_for_user: false, include_quorum_employees: true } : {},
        ),
        actionGroups.patchOrgTypes,
        id,
        org,
    )

export const deactivateOrg = (id) =>
    djangioActionCreators.resourcePatchAction(
        Organization.objects.filter(Userdata.is_staff ? { default_for_user: false } : {}),
        deactivateOrgActionTypes,
        id,
        // decorator in API needs organization in this format
        {
            actionName: "deactivate_org",
            id,
        },
        {
            payload: id,
        },
    )

export const getDefaultTeamByOrg = (orgId) => (dispatch) => {
    dispatch({ type: actionTypes.GET_TEAM_START })
    return DjangIO.app.userdata.models.Team.objects
        .filter({ organization: orgId, default: true, default_for_user: false })
        .GET()
        .then((response) => {
            dispatch({
                type: actionTypes.GET_TEAM_SUCCESS,
                response: response.objects[0],
            })
        })
        .catch((error) => {
            dispatch({ type: actionTypes.GET_TEAM_FAIL, error })
            throw error
        })
}

export const getOrgUserCount = (orgId) => (dispatch) => {
    return DjangIO.app.userdata.models.QuorumUser.objects
        .filter({ default_for_user: false, for_org: orgId })
        .GET()
        .then((response) => {
            dispatch(editOrgUiState("orgUserCount", response.objects.length))
        })
}

/*
This provides one action creator which is able to modify
the organization Map in the OrgManagement map. They are sent as
key, value pairs.
*/
export const editOrg = (key, value) => ({ type: actionTypes.EDIT_ORG, key, value })

export const editOrgSetIn = (path, value) => ({ type: actionTypes.EDIT_ORG_SET_IN, path, value })

export const editOrgUiState = (key, value) => ({ type: actionTypes.EDIT_ORG_UI_STATE, key, value })

/*
This provides one action creator which is able to modify
the OrgPermissions Map in the OrgManagement Map
*/
export const addProduct = (productObj) => ({
    type: actionTypes.ADD_PRODUCT,
    productObj,
})
// meant to replace addProduct
export const editProducts = (productObj) => ({
    type: actionTypes.EDIT_PRODUCTS,
    productObj,
})

/*
This provides one action creator which is able to modify
the AccountInfo Map in the store. They are sent as
key, value pairs. Then the reducer updates that key
and value inside of AccountInfo.
*/
export const changeInfo = (key, value) => ({ type: actionTypes.CHANGE_INFO, key, value })

export const setPrimaryProductUsed = () => ({ type: actionTypes.SET_PRIMARY_PRODUCT_USED })

export const setDefaultAllApplicableUserTypes = () => ({ type: actionTypes.SET_DEFAULT_ALL_APPLICABLE_USER_TYPES })

export const editUserUiState = (key, value) => ({ type: actionTypes.EDIT_USER_UI_STATE, key, value })

export const editFilters = (keyPath, value) => ({ type: actionTypes.EDIT_FILTERS, keyPath, value })

export const userFormSubmitSearchify =
    (fieldName, { searchifyFilters }) =>
    (dispatch) => {
        dispatch(editFilters(["AccountInfo", "user", fieldName, "added_ids"], searchifyFilters.addedIds))
        dispatch(editFilters(["AccountInfo", "user", fieldName, "deleted_ids"], searchifyFilters.deletedIds))
        dispatch(editFilters(["AccountInfo", "user", fieldName, "filters"], searchifyFilters.querysetList))
    }

export const userFormSubmitBills = userFormSubmitSearchify.bind(null, "bill_default_search_filters")
export const userFormSubmitDocuments = userFormSubmitSearchify.bind(null, "document_default_search_filters")
export const userFormSubmitPersons = userFormSubmitSearchify.bind(null, "person_default_search_filters")
export const userFormSubmitStaffers = userFormSubmitSearchify.bind(null, "staffer_default_search_filters")
export const userFormSubmitSupporters = userFormSubmitSearchify.bind(null, "supporter_default_search_filters")
export const userFormSubmitNotes = userFormSubmitSearchify.bind(null, "note_default_search_filters")
export const userFormSubmitEvents = userFormSubmitSearchify.bind(null, "event_default_search_filters")
export const userFormSubmitVotes = userFormSubmitSearchify.bind(null, "vote_default_search_filters")
export const userFormSubmitAmendments = userFormSubmitSearchify.bind(null, "amendment_default_search_filters")
export const userFormSubmitPublicOrganizations = userFormSubmitSearchify.bind(
    null,
    "public_organization_default_search_filters",
)
export const userFormSubmitAttachments = userFormSubmitSearchify.bind(null, "attachment_default_search_filters")
export const userFormSubmitAlerts = userFormSubmitSearchify.bind(null, "alert_default_search_filters")
export const userFormSubmitArticles = userFormSubmitSearchify.bind(null, "article_default_search_filters")
export const userFormSubmitPressContacts = userFormSubmitSearchify.bind(null, "press_contact_default_search_filters")

export const loadUser = (userId) => (dispatch) => {
    dispatch({ type: actionTypes.GET_USER_START })

    const shouldUseDefaultForUser = getIsLoadingUserdataUser(Userdata.id, userId)
    const getUser = DjangIO.app.userdata.models.QuorumUser.objects
        .filter({ default_for_user: shouldUseDefaultForUser })
        .get(userId)
        .GET()

    const getUserInfo = DjangIO.app.userdata.models.UserInfo.objects
        .filter({ default_for_user: shouldUseDefaultForUser })
        .full_dehydrate("organization")
        .full_dehydrate("bill_default_search_filters")
        .full_dehydrate("document_default_search_filters")
        .full_dehydrate("person_default_search_filters")
        .full_dehydrate("staffer_default_search_filters")
        .full_dehydrate("supporter_default_search_filters")
        .full_dehydrate("note_default_search_filters")
        .full_dehydrate("event_default_search_filters")
        .full_dehydrate("vote_default_search_filters")
        .full_dehydrate("amendment_default_search_filters")
        .full_dehydrate("public_organization_default_search_filters")
        .full_dehydrate("attachment_default_search_filters")
        .full_dehydrate("alert_default_search_filters")
        .full_dehydrate("article_default_search_filters")
        .full_dehydrate("press_contact_default_search_filters")
        .filter({ user: userId })
        .GET()

    const getDefaultTeam = DjangIO.app.userdata.models.Team.objects
        .filter({
            default: true,
            members: userId,
            default_for_user: shouldUseDefaultForUser,
        })
        .GET()

    return Promise.all([getUser, getUserInfo, getDefaultTeam])
        .then(([user, userInfoObjects, defaultTeamObjects]) => {
            const userInfo = userInfoObjects.objects[0]
            const defaultTeam = defaultTeamObjects.objects[0]
            const userRegions = DjangIO.app.models.Region.items().filter((region) =>
                userInfo.permissioned_regions.includes(region.value),
            )
            dispatch({
                type: actionTypes.GET_USER_SUCCESS,
                response: {
                    amendment_default_search_filters: userInfo.amendment_default_search_filters,
                    attachment_default_search_filters: userInfo.attachment_default_search_filters,
                    bill_default_search_filters: userInfo.bill_default_search_filters,
                    can_access_all_org_regions: userInfo.can_access_all_org_regions,
                    can_access_bulk_uploads: userInfo.can_access_bulk_uploads,
                    can_access_custom_fields_management: userInfo.can_access_custom_fields_management,
                    bulk_action_max_size: userInfo.bulk_action_max_size,
                    can_access_media_monitoring: userInfo.can_access_media_monitoring,
                    can_access_media_relations: userInfo.can_access_media_relations,
                    can_access_grassroots: userInfo.can_access_grassroots,
                    can_access_issue_management: userInfo.can_access_issue_management,
                    can_access_campaign_finance: userInfo.can_access_campaign_finance,
                    can_access_notes: userInfo.can_access_notes,
                    can_access_outbox: userInfo.can_access_outbox,
                    can_access_pac: userInfo.can_access_pac,
                    can_access_pac_management: userInfo.can_access_pac_management,
                    can_access_pac_2_data: userInfo.can_access_pac_2_data,
                    can_view_pac_2_receipts: userInfo.can_view_pac_2_receipts,
                    can_access_texting: userInfo.can_access_texting,
                    can_alter_pac_transactions: userInfo.can_alter_pac_transactions,
                    can_change_action_center_settings: userInfo.can_change_action_center_settings,
                    can_only_create_single_user_issues: userInfo.can_only_create_single_user_issues,
                    can_see_supporters_based_on_region: userInfo.can_see_supporters_based_on_region,
                    can_see_any_previous_outbox_email: userInfo.can_see_any_previous_outbox_email,
                    can_see_any_previous_outbound_texts: userInfo.can_see_any_previous_outbound_texts,
                    can_send_from_any_outbox: userInfo.can_send_from_any_outbox,
                    can_see_supporters_and_custom_orgs: userInfo.can_see_supporters_and_custom_orgs,
                    can_view_pac_receipts: userInfo.can_view_pac_receipts,
                    can_view_pac_disbursements: userInfo.can_view_pac_disbursements,
                    can_access_axios_articles: userInfo.can_access_axios_articles,
                    default_search_data_type: userInfo.default_search_data_type,
                    default_limited_to_teams: userInfo.default_limited_to_teams,
                    document_default_search_filters: userInfo.document_default_search_filters,
                    email: user.email,
                    event_default_search_filters: userInfo.event_default_search_filters,
                    first_name: user.first_name,
                    id: user.id,
                    is_admin: userInfo.org_admin,
                    last_name: user.last_name,
                    note_default_search_filters: userInfo.note_default_search_filters,
                    other_personas: userInfo.other_personas,
                    person_default_search_filters: userInfo.person_default_search_filters,
                    persona: userInfo.persona,
                    press_contact_default_search_filters: userInfo.press_contact_default_search_filters,
                    public_organization_default_search_filters: userInfo.public_organization_default_search_filters,
                    search_data_type_order: userInfo.search_data_type_order,
                    should_hide_team_permissionining_in_forms: userInfo.should_hide_team_permissionining_in_forms,
                    sidebar_settings_list: userInfo.sidebar_settings_list,
                    single_login: userInfo.single_login,
                    staffer_default_search_filters: userInfo.staffer_default_search_filters,
                    supporter_default_search_filters: userInfo.supporter_default_search_filters,
                    teams: user.teams,
                    two_factor_status: userInfo.two_factor_status,
                    user_regions: userRegions,
                    username: user.username,
                    user_persona: userInfo.user_persona,
                    vote_default_search_filters: userInfo.vote_default_search_filters,
                    email_signature: userInfo.email_signature,
                    alert_default_search_filters: userInfo.alert_default_search_filters,
                    article_default_search_filters: userInfo.article_default_search_filters,
                    user_role: userInfo.user_role,
                    available_pacc_pacs: userInfo.available_pacc_pacs,
                },
            })
            dispatch(editUserUiState("oldTwoFactorStatus", userInfo.two_factor_status))
            dispatch(editUserUiState("defaultTeam", defaultTeam))
            // save the info for the org the user belongs to in the store
            dispatch({
                type: actionTypes.GET_ORG_SUCCESS,
                response: userInfo.organization,
            })
        })
        .catch((err) => {
            dispatch({ type: actionTypes.GET_USER_FAIL })
            BACKENDERROR(err)
        })
}

export const editTeam = (key, value) => ({ type: actionTypes.EDIT_TEAM, key, value })

export const editTeamUiState = (key, value) => ({ type: actionTypes.EDIT_TEAM_UI_STATE, key, value })

// changes to a new design
export const changeDesign = (orgDesign) => ({
    type: actionTypes.CHANGE_DESIGN,
    OrgDesign: orgDesign.toJS ? orgDesign : Immutable.fromJS(orgDesign),
})

// changes to a new team
export const changeTeam = (team) => ({
    type: actionTypes.CHANGE_TEAM,
    team,
})

export const resetForm = () => ({
    type: actionTypes.RESET_FORM,
})

export const deleteDesign = () => ({
    type: actionTypes.DELETE_DESIGN,
})

// Toggles whether or not the color
// picker can be seen
export const togglePicker = (pickerID) => ({
    type: actionTypes.TOGGLE_PICKER,
    picker: `${pickerID}Picker`,
})

export const toggleCreateMode = () => ({
    type: actionTypes.TOGGLE_CREATE_MODE,
})

export const changeShouldUseActionCenterHeaderColor = (value) => ({
    type: actionTypes.CHANGE_DESIGN,
    OrgDesign: { should_use_action_center_header_color: value },
})

export const changeFont = (UpdateObject) => ({
    type: actionTypes.CHANGE_DESIGN,
    OrgDesign: {
        font_family: UpdateObject.id,
        font_link: `@font-face { font-family: "${UpdateObject.id}"; src: url(${UpdateObject.url}) format("truetype"); }`,
    },
})

// Changes the name of an organization design
export const changeName = (NewName) => ({
    type: actionTypes.CHANGE_DESIGN,
    OrgDesign: { design_name: NewName.target.value },
})

export const changeImage = (newImage, id) => ({
    type: actionTypes.CHANGE_DESIGN,
    OrgDesign: { [id]: newImage.image_url },
})

// Handles both ways a color can be sent to it
export const changeColor = (color, id) => {
    let hex
    if (color.hex === undefined) {
        hex = color.target.value
    } else {
        hex = color.hex
    }
    return {
        type: actionTypes.CHANGE_DESIGN,
        OrgDesign: { [id]: hex },
    }
}

// Fetch a design from the database as the page loads
export const getDefaultDesign = () =>
    djangioActionCreators.resourceGetList(
        DjangIO.app.userdata.models.OrganizationDesign.objects.filter({ default: true }),
        actionGroups.getDefaultDesign,
    )

// Create a new design and toggle the settings
// which indicate that setting on
export const createOrgDesign = (id, orgDesign) => (dispatch) => {
    const createDict = {
        organization: DjangIO.app.userdata.models.Organization.foreignKey(Userdata.organization_id),
        ...orgDesign.toJS(),
    }
    return DjangIO.app.userdata.models.OrganizationDesign.objects
        .create(createDict)
        .POST()
        .then((OrgDesign) => {
            dispatch({
                type: actionTypes.CHANGE_DESIGN,
                OrgDesign,
            })
            dispatch({ type: actionTypes.TOGGLE_CREATE_MODE })
            return OrgDesign
        })
}

/**
 * Patch an organization design
 * @param {Integer} id - the id of the organization design
 * @param {Map} [orgDesign] A map containing data about the org design
 * @returns {Promise}
 */
export const patchOrgDesign = (id, orgDesign) =>
    djangioActionCreators.resourcePatchDetail(
        DjangIO.app.userdata.models.OrganizationDesign,
        actionGroups.patchOrgDesignTypes,
        id,
        orgDesign.toJS(),
        { id },
    )

/**
 * Patch an organization design
 * @param {Integer} id - the id of the organization design
 * @returns {Promise}
 */
export const deleteOrgDesign = (id) => (dispatch) => {
    dispatch({ type: actionTypes.DELETE_DESIGN })
    return DjangIO.app.userdata.models.OrganizationDesign.objects
        .get(id)
        .update({
            archived: true,
        })
        .PATCH()
}

export const userFormStartSearchify = () => ({ type: actionTypes.USER_FORM_START_SEARCHIFY })
